import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import {
  FileText, Download, ExternalLink, Search, RefreshCw,
  Loader2, Building2, User, Database, Info,
  ChevronRight, ChevronDown, Receipt, Percent, Landmark, Banknote, Calendar,
  TrendingUp, AlertTriangle, CheckCircle2, Table2, Truck, CreditCard
} from 'lucide-react';

/**
 * AdminTaxInvoices — Internal SSOT view (iter-252-AH Finance Phase B-3/B-4)
 *
 * Tabs:
 *  1. Tax Invoices       — `/api/invoices/?include_parents&from_date&to_date`
 *                          with parent→children expansion and Zoho mirror toggle.
 *  2. Commission Invoices — `/api/commission-invoices/`
 *  3. TDS/TCS Ledgers     — `/api/tax-management/admin/analytics` (A/B/C/D KPIs)
 *                          + `/api/tcs-logs/` row-level detail.
 *
 * Date filter is shared across tabs (default: current FY → today).
 */

// ── Date helpers ─────────────────────────────────────────────────────────
const todayISO = () => new Date().toISOString().slice(0, 10);
const currentFYStartISO = () => {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `${year}-04-01`;
};
const formatINR = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const formatDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return d;
  }
};

// ── Date Range filter (shared) ───────────────────────────────────────────
const DateRangeFilter = ({ fromDate, toDate, setFromDate, setToDate, onApply, onReset }) => (
  <Card data-testid="admin-tax-date-filter">
    <CardContent className="p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col">
          <Label htmlFor="tax-from-date" className="text-xs text-gray-600 mb-1 flex items-center gap-1">
            <Calendar className="h-3 w-3" /> From
          </Label>
          <Input
            id="tax-from-date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-40"
            data-testid="admin-tax-from-date"
          />
        </div>
        <div className="flex flex-col">
          <Label htmlFor="tax-to-date" className="text-xs text-gray-600 mb-1 flex items-center gap-1">
            <Calendar className="h-3 w-3" /> To
          </Label>
          <Input
            id="tax-to-date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-40"
            data-testid="admin-tax-to-date"
          />
        </div>
        <Button onClick={onApply} size="sm" data-testid="admin-tax-apply-dates">
          Apply
        </Button>
        <Button onClick={onReset} variant="outline" size="sm" data-testid="admin-tax-reset-dates">
          Reset to FY
        </Button>
        <div className="ml-auto text-xs text-gray-500">
          Current FY: <span className="font-medium">{currentFYStartISO()}</span> → today
        </div>
      </div>
    </CardContent>
  </Card>
);

// ── Tax Invoices Tab (with parent→children expansion) ────────────────────
const TaxInvoicesTab = ({ apiCall, toast, fromDate, toDate }) => {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showZohoMirror, setShowZohoMirror] = useState(false);
  const [expandedParents, setExpandedParents] = useState({});

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      if (showZohoMirror) {
        const data = await apiCall('/api/zoho/invoices/admin/list');
        const rows = (data?.invoices || []).map((inv) => ({
          source: 'zoho',
          key: inv.id || inv.zoho_invoice_id,
          invoice_number: inv.zoho_invoice_number || 'N/A',
          order_id: inv.order_id || '',
          order_number: inv.order_id ? inv.order_id.slice(0, 8) : '',
          vendor_id: inv.vendor_id || '',
          vendor_name: inv.vendor_name || 'Unknown Vendor',
          customer_name: inv.customer_name || 'N/A',
          customer_email: '',
          total_amount: Number(inv.total_amount || 0),
          invoice_title: 'ZOHO TAX INVOICE',
          tax_type: inv.tax_type || '',
          financial_year: inv.financial_year || '',
          generated_at: inv.created_at || inv.generated_at || '',
          zoho_invoice_url: inv.zoho_invoice_url || null,
          zoho_status: inv.zoho_status || null,
          is_parent_order: false,
          parent_order_id: null,
          is_logistics_fee: false,
        }));
        setInvoices(rows);
      } else {
        const qs = new URLSearchParams({
          page: '1',
          limit: '500',
          include_parents: 'true',
        });
        if (fromDate) qs.append('from_date', fromDate);
        if (toDate) qs.append('to_date', toDate);
        const data = await apiCall(`/api/invoices/?${qs.toString()}`);
        const rows = (data?.invoices || []).map((inv) => ({
          source: 'internal',
          key: `${inv.invoice_number}-${inv.order_id}`,
          invoice_number: inv.invoice_number || 'N/A',
          order_id: inv.order_id || '',
          order_number: inv.order_number || '',
          vendor_id: inv.vendor_id || '',
          vendor_name: '',
          customer_name: inv.customer_name || '',
          customer_email: inv.customer_email || '',
          total_amount: Number(inv.total_amount || 0),
          invoice_title: inv.invoice_title || 'TAX INVOICE',
          tax_type: inv.tax_type || '',
          financial_year: inv.financial_year || '',
          generated_at: inv.generated_at || '',
          zoho_invoice_url: null,
          zoho_status: null,
          is_parent_order: !!inv.is_parent_order,
          parent_order_id: inv.parent_order_id || null,
          is_logistics_fee: !!inv.is_logistics_fee,
        }));
        setInvoices(rows);
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      toast({
        title: 'Error',
        description: showZohoMirror
          ? 'Failed to load Zoho mirror invoices'
          : 'Failed to load internal tax invoices',
        variant: 'destructive',
      });
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [apiCall, showZohoMirror, fromDate, toDate, toast]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const filtered = useMemo(
    () =>
      invoices.filter((inv) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
          (inv.invoice_number || '').toLowerCase().includes(q) ||
          (inv.order_id || '').toLowerCase().includes(q) ||
          (inv.order_number || '').toLowerCase().includes(q) ||
          (inv.vendor_name || '').toLowerCase().includes(q) ||
          (inv.customer_name || '').toLowerCase().includes(q) ||
          (inv.customer_email || '').toLowerCase().includes(q)
        );
      }),
    [invoices, searchQuery],
  );

  // Group child rows by their parent_order_id when viewing internal SSOT.
  const { parents, childrenByParent, orphans } = useMemo(() => {
    if (showZohoMirror) {
      return { parents: filtered, childrenByParent: {}, orphans: [] };
    }
    const parentsArr = filtered.filter((r) => r.is_parent_order);
    const parentOrderIds = new Set(parentsArr.map((p) => p.order_id));
    const grouped = {};
    const orphansArr = [];
    for (const r of filtered) {
      if (r.is_parent_order) continue;
      if (r.parent_order_id && parentOrderIds.has(r.parent_order_id)) {
        (grouped[r.parent_order_id] = grouped[r.parent_order_id] || []).push(r);
      } else {
        orphansArr.push(r);
      }
    }
    return { parents: parentsArr, childrenByParent: grouped, orphans: orphansArr };
  }, [filtered, showZohoMirror]);

  const toggleParent = (orderId) =>
    setExpandedParents((p) => ({ ...p, [orderId]: !p[orderId] }));

  const handleDownloadPDF = async (orderId, invoiceNumber, isParent) => {
    try {
      const endpoint = isParent
        ? `/api/invoices/consolidated/${orderId}`
        : `/api/invoices/${orderId}/pdf`;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}${endpoint}`,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } },
      );
      if (!response.ok) throw new Error('Failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber || 'invoice'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to download PDF',
        variant: 'destructive',
      });
    }
  };

  const renderRow = (inv, depth = 0) => {
    const isParent = inv.is_parent_order && !showZohoMirror;
    const children = childrenByParent[inv.order_id] || [];
    const hasChildren = isParent && children.length > 0;
    const expanded = expandedParents[inv.order_id];
    return (
      <React.Fragment key={inv.key}>
        <tr
          className={`border-b hover:bg-gray-50 ${depth > 0 ? 'bg-gray-50/60' : ''}`}
          data-testid={`admin-tax-invoice-row-${inv.invoice_number}`}
        >
          <td className="px-4 py-3" style={{ paddingLeft: `${1 + depth * 1.5}rem` }}>
            <div className="flex items-center gap-2">
              {hasChildren ? (
                <button
                  onClick={() => toggleParent(inv.order_id)}
                  className="p-0.5 hover:bg-gray-200 rounded"
                  data-testid={`admin-tax-invoice-expand-${inv.order_id}`}
                  aria-label={expanded ? 'Collapse' : 'Expand'}
                >
                  {expanded ? (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              ) : (
                <span className="inline-block w-5" />
              )}
              <div>
                <div className="font-medium">{inv.invoice_number}</div>
                <div className="text-xs text-gray-500 flex items-center gap-1.5">
                  <span>{inv.invoice_title}</span>
                  {isParent && (
                    <Badge variant="secondary" className="text-[10px] py-0">
                      Parent · {children.length} child{children.length !== 1 ? 'ren' : ''}
                    </Badge>
                  )}
                  {inv.is_logistics_fee && (
                    <Badge variant="outline" className="text-[10px] py-0 border-amber-400 text-amber-700">
                      Aniyor Logistics
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </td>
          <td className="px-4 py-3">
            <div className="font-medium">
              {inv.order_number || (inv.order_id ? `${inv.order_id.slice(0, 8)}…` : '—')}
            </div>
          </td>
          {showZohoMirror && (
            <td className="px-4 py-3">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-gray-400" />
                <span>{inv.vendor_name}</span>
              </div>
            </td>
          )}
          <td className="px-4 py-3">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-gray-400" />
              <div className="flex flex-col">
                <span>{inv.customer_name || '—'}</span>
                {inv.customer_email && (
                  <span className="text-xs text-gray-500">{inv.customer_email}</span>
                )}
              </div>
            </div>
          </td>
          <td className="px-4 py-3 font-medium">{formatINR(inv.total_amount)}</td>
          <td className="px-4 py-3">
            {inv.tax_type ? (
              <Badge variant="outline" className="text-xs">
                {inv.tax_type}
              </Badge>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </td>
          <td className="px-4 py-3 text-gray-500">{inv.financial_year || '—'}</td>
          <td className="px-4 py-3 text-gray-500">{formatDate(inv.generated_at)}</td>
          <td className="px-4 py-3">
            <div className="flex gap-1">
              {!showZohoMirror && inv.order_id && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDownloadPDF(inv.order_id, inv.invoice_number, isParent)}
                  data-testid={`admin-tax-invoice-pdf-${inv.invoice_number}`}
                  title={isParent ? 'Download consolidated receipt' : 'Download PDF'}
                >
                  <Download className="h-4 w-4" />
                </Button>
              )}
              {showZohoMirror && inv.zoho_invoice_url && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => window.open(inv.zoho_invoice_url, '_blank')}
                  title="Open in Zoho"
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              )}
            </div>
          </td>
        </tr>
        {hasChildren && expanded && children.map((c) => renderRow(c, depth + 1))}
      </React.Fragment>
    );
  };

  const visibleCount =
    parents.length + Object.values(childrenByParent).flat().length + orphans.length;

  return (
    <div className="space-y-6">
      {/* SSOT Banner */}
      <Card
        className={
          showZohoMirror
            ? 'border-amber-200 bg-amber-50'
            : 'border-blue-200 bg-blue-50'
        }
        data-testid="admin-tax-invoices-source-banner"
      >
        <CardContent className="p-4 flex items-start gap-3">
          {showZohoMirror ? (
            <ExternalLink className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
          ) : (
            <Database className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          )}
          <div className="flex-1">
            <div className="text-sm font-medium">
              {showZohoMirror
                ? 'Reading from: Zoho Books mirror (legacy)'
                : "Reading from: ANIYOR's internal invoice ledger (SSOT)"}
            </div>
            <div className="text-xs text-gray-600 mt-1 flex items-start gap-1">
              <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <span>
                {showZohoMirror
                  ? 'These are mirrored to Zoho Books. May lag behind internal records.'
                  : 'Canonical tax invoices from db.invoices. Parent rollups (with Aniyor logistics-fee child invoices) are expandable.'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Label htmlFor="zoho-mirror-toggle" className="text-xs text-gray-700">
              Show Zoho mirror
            </Label>
            <Switch
              id="zoho-mirror-toggle"
              checked={showZohoMirror}
              onCheckedChange={(v) => setShowZohoMirror(v === true)}
              data-testid="admin-tax-invoices-zoho-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by invoice number, order #, vendor, customer, or email..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="admin-tax-invoices-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12" data-testid="admin-tax-invoices-loading">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : visibleCount === 0 ? (
        <Card className="p-8 text-center" data-testid="admin-tax-invoices-empty">
          <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-700">No Tax Invoices Found</h3>
          <p className="text-gray-500 text-sm mt-1">
            {searchQuery
              ? 'Try a different search term'
              : showZohoMirror
              ? 'No Zoho-mirrored invoices'
              : 'No internal invoices in this date range'}
          </p>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>All Invoices</CardTitle>
            <CardDescription data-testid="admin-tax-invoices-count">
              {visibleCount} invoice{visibleCount !== 1 ? 's' : ''} found
              {!showZohoMirror &&
                ` · ${parents.length} parent rollup${parents.length !== 1 ? 's' : ''}`}
              {!showZohoMirror && ' · internal SSOT'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-tax-invoices-table">
                <thead className="border-b">
                  <tr className="text-left text-gray-500">
                    <th className="px-4 py-3">Invoice #</th>
                    <th className="px-4 py-3">Order</th>
                    {showZohoMirror && <th className="px-4 py-3">Vendor</th>}
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Tax Type</th>
                    <th className="px-4 py-3">FY</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {parents.map((p) => renderRow(p, 0))}
                  {orphans.map((o) => renderRow(o, 0))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ── Commission Invoices Tab ──────────────────────────────────────────────
const CommissionInvoicesTab = ({ apiCall, toast }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCall('/api/commission-invoices/?limit=500');
      setRows(Array.isArray(data) ? data : (data?.invoices || []));
    } catch (error) {
      console.error('Commission invoices fetch failed:', error);
      toast({
        title: 'Error',
        description: 'Failed to load commission invoices',
        variant: 'destructive',
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [apiCall, toast]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const filtered = useMemo(() => {
    if (!searchQuery) return rows;
    const q = searchQuery.toLowerCase();
    return rows.filter(
      (r) =>
        (r.invoice_number || '').toLowerCase().includes(q) ||
        (r.vendor_name || '').toLowerCase().includes(q) ||
        (r.vendor_id || '').toLowerCase().includes(q),
    );
  }, [rows, searchQuery]);

  const handleDownload = async (invoiceId, invoiceNumber) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/commission-invoices/${invoiceId}/download`,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } },
      );
      if (!response.ok) throw new Error('Failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber || 'commission-invoice'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Error', description: 'Failed to download PDF', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-purple-200 bg-purple-50" data-testid="commission-invoices-banner">
        <CardContent className="p-4 flex items-start gap-3">
          <Receipt className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-medium">Commission Invoices issued by Aniyor to vendors</div>
            <div className="text-xs text-gray-600 mt-1">
              Source: <code>db.commission_invoices</code> · GST-bearing invoices for marketplace commission earned.
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by invoice number, vendor name…"
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="commission-invoices-search"
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12" data-testid="commission-invoices-loading">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center" data-testid="commission-invoices-empty">
          <Receipt className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-700">No commission invoices</h3>
          <p className="text-gray-500 text-sm mt-1">
            Commission invoices are generated periodically for vendors.
          </p>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Commission Invoices</CardTitle>
            <CardDescription data-testid="commission-invoices-count">
              {filtered.length} invoice{filtered.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="commission-invoices-table">
                <thead className="border-b">
                  <tr className="text-left text-gray-500">
                    <th className="px-4 py-3">Invoice #</th>
                    <th className="px-4 py-3">Vendor</th>
                    <th className="px-4 py-3">Period</th>
                    <th className="px-4 py-3">Subtotal</th>
                    <th className="px-4 py-3">GST</th>
                    <th className="px-4 py-3">Grand Total</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr
                      key={r.id}
                      className="border-b hover:bg-gray-50"
                      data-testid={`commission-invoice-row-${r.invoice_number}`}
                    >
                      <td className="px-4 py-3 font-medium">{r.invoice_number}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-gray-400" />
                          <span>{r.vendor_name || '—'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">
                        {formatDate(r.period_start)} → {formatDate(r.period_end)}
                      </td>
                      <td className="px-4 py-3">{formatINR(r.subtotal)}</td>
                      <td className="px-4 py-3 text-gray-600">{formatINR(r.total_gst)}</td>
                      <td className="px-4 py-3 font-medium">{formatINR(r.grand_total)}</td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={r.status === 'paid' ? 'default' : 'secondary'}
                          className="text-xs capitalize"
                        >
                          {r.status || 'pending'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-500">{formatDate(r.invoice_date)}</td>
                      <td className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(r.id, r.invoice_number)}
                          data-testid={`commission-invoice-pdf-${r.invoice_number}`}
                          title="Download PDF"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ── Projected GST Tab ────────────────────────────────────────────────────
const ProjectedGstTab = ({ apiCall, toast, fromDate, toDate }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (fromDate) qs.append('start_date', fromDate);
      if (toDate) qs.append('end_date', toDate);
      const res = await apiCall(`/api/tax-management/admin/projected-gst?${qs.toString()}`);
      setData(res);
    } catch (err) {
      console.error('Projected GST fetch failed:', err);
      toast({ title: 'Error', description: 'Failed to load projected GST', variant: 'destructive' });
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [apiCall, fromDate, toDate, toast]);

  useEffect(() => { fetch(); }, [fetch]);

  const gst = data?.projected_gst || {};
  const totals = gst.totals || {};

  const DriftBadge = ({ drift }) => {
    if (drift === 0) return <span className="text-xs text-gray-400">no drift</span>;
    if (drift > 0)
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
          <AlertTriangle className="h-3 w-3" /> +₹{drift.toLocaleString('en-IN', { minimumFractionDigits: 2 })} unrecognised
        </span>
      );
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
        ₹{Math.abs(drift).toLocaleString('en-IN', { minimumFractionDigits: 2 })} over-recognised
      </span>
    );
  };

  const GstRow = ({ item, testid }) => (
    <tr className="border-b hover:bg-gray-50" data-testid={testid}>
      <td className="px-4 py-3">
        <div className="font-medium text-sm">{item.label}</div>
        <div className="text-[10px] text-gray-400 mt-0.5">{item.source}</div>
      </td>
      <td className="px-4 py-3 text-center text-gray-500 text-sm">{item.invoice_count ?? '—'}</td>
      <td className="px-4 py-3 font-medium text-sm">
        ₹{Number(item.forecasted || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        ₹{Number(item.recognised || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
      </td>
      <td className="px-4 py-3">
        <DriftBadge drift={item.drift ?? 0} />
      </td>
    </tr>
  );

  return (
    <div className="space-y-6" data-testid="projected-gst-tab">
      {/* Info banner */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4 flex items-start gap-3">
          <TrendingUp className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <div className="font-medium text-blue-900">Projected GST — Accrual Lane (not delivery-gated)</div>
            <div className="text-xs text-blue-700 mt-1">
              GST liability accrues at invoice creation, independent of shipment delivery.
              <strong> Forecasted</strong> = from canonical invoice sources.
              <strong> Recognised</strong> = booked in <code>tax_ledger</code> (status=recognised).
              Positive drift = GST has accrued but the forward-projection writers haven&apos;t run yet.
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16" data-testid="projected-gst-loading">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : (
        <>
          {/* Summary KPI row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="border-l-4 border-l-indigo-500" data-testid="kpi-total-forecasted">
              <CardContent className="p-4">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Total Forecasted GST</div>
                <div className="text-2xl font-bold mt-1 text-indigo-700" data-testid="kpi-total-forecasted-value">
                  ₹{Number(totals.total_forecasted || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-gray-400 mt-1">Accrued from invoice sources</div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-500" data-testid="kpi-total-recognised">
              <CardContent className="p-4">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Total Recognised GST</div>
                <div className="text-2xl font-bold mt-1 text-emerald-700" data-testid="kpi-total-recognised-value">
                  ₹{Number(totals.total_recognised || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-gray-400 mt-1">Booked in tax_ledger</div>
              </CardContent>
            </Card>
            <Card
              className={`border-l-4 ${(totals.total_drift || 0) > 0 ? 'border-l-amber-500' : 'border-l-gray-300'}`}
              data-testid="kpi-total-drift"
            >
              <CardContent className="p-4">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Total Drift</div>
                <div className={`text-2xl font-bold mt-1 ${(totals.total_drift || 0) > 0 ? 'text-amber-700' : 'text-gray-500'}`}
                  data-testid="kpi-total-drift-value">
                  ₹{Number(Math.abs(totals.total_drift || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {(totals.total_drift || 0) === 0
                    ? 'No drift — ledger is in sync'
                    : (totals.total_drift || 0) > 0
                    ? 'Unrecognised (run finance-backfill)'
                    : 'Over-recognised'}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detail breakdown table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-indigo-600" />
                GST Component Breakdown
              </CardTitle>
              <CardDescription>
                Forecasted vs Recognised per GST category. Positive drift = accrued but not yet booked.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="projected-gst-table">
                  <thead className="border-b">
                    <tr className="text-left text-gray-500 text-xs uppercase tracking-wide">
                      <th className="px-4 py-3">GST Component</th>
                      <th className="px-4 py-3 text-center">Invoices</th>
                      <th className="px-4 py-3">Forecasted</th>
                      <th className="px-4 py-3">Recognised</th>
                      <th className="px-4 py-3">Drift</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gst.commission_gst && (
                      <GstRow item={gst.commission_gst} testid="projected-gst-row-commission" />
                    )}
                    {gst.logistics_gst && (
                      <GstRow item={gst.logistics_gst} testid="projected-gst-row-logistics" />
                    )}
                    {gst.subscription_gst && (
                      <GstRow item={gst.subscription_gst} testid="projected-gst-row-subscription" />
                    )}
                    {gst.other_platform_gst && (
                      <GstRow item={gst.other_platform_gst} testid="projected-gst-row-other" />
                    )}
                    {/* Totals row */}
                    <tr className="bg-gray-50 font-semibold border-t-2 border-gray-200">
                      <td className="px-4 py-3 text-sm">Total</td>
                      <td className="px-4 py-3" />
                      <td className="px-4 py-3 text-indigo-700">
                        ₹{Number(totals.total_forecasted || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-emerald-700">
                        ₹{Number(totals.total_recognised || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3">
                        <DriftBadge drift={totals.total_drift ?? 0} />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Explanation of zero forecasted */}
          {totals.total_forecasted === 0 && (
            <Card className="border-amber-200 bg-amber-50" data-testid="projected-gst-zero-banner">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <div className="font-medium text-amber-900">Forecasted GST is ₹0.00 for this period</div>
                  <div className="text-xs text-gray-700 mt-1">
                    This means <code>commission_invoices</code>, <code>subscription_invoices</code>, and
                    logistics-fee orders are all empty or outside the selected date range. Run the{' '}
                    <code>finance-reconciliation-matrix</code> maintenance template to verify which orders
                    have invoices. If the ledgers are genuinely empty, the forward-projection writers
                    (commission invoice generator, subscription billing) have not run yet.
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {data?.note && (
            <p className="text-xs text-gray-400 italic" data-testid="projected-gst-note">{data.note}</p>
          )}
        </>
      )}
    </div>
  );
};

// ── KPI tile ─────────────────────────────────────────────────────────────
const KpiTile = ({ label, value, hint, icon: Icon, accent, testid }) => (
  <Card data-testid={testid} className={`border-l-4 ${accent}`}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
          <div className="text-2xl font-bold mt-1" data-testid={`${testid}-value`}>
            {value}
          </div>
          {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
        </div>
        <Icon className="h-5 w-5 text-gray-400" />
      </div>
    </CardContent>
  </Card>
);

// ── TDS/TCS Ledgers Tab ──────────────────────────────────────────────────
const TdsTcsTab = ({ apiCall, toast, fromDate, toDate }) => {
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState({});
  const [tcsLogs, setTcsLogs] = useState([]);

  const fetchAnalytics = useCallback(async () => {
    setLoadingAnalytics(true);
    try {
      const qs = new URLSearchParams();
      if (fromDate) qs.append('start_date', fromDate);
      if (toDate) qs.append('end_date', toDate);
      const data = await apiCall(`/api/tax-management/admin/analytics?${qs.toString()}`);
      const a = data?.analytics || {};
      setSummary(a.summary || null);
      setMonthly(a.monthly_breakdown || {});
    } catch (error) {
      console.error('Tax analytics fetch failed:', error);
      toast({
        title: 'Error',
        description: 'Failed to load tax analytics',
        variant: 'destructive',
      });
      setSummary(null);
      setMonthly({});
    } finally {
      setLoadingAnalytics(false);
    }
  }, [apiCall, fromDate, toDate, toast]);
  const fetchTcsLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const data = await apiCall('/api/tcs-logs/?limit=200');
      setTcsLogs(Array.isArray(data) ? data : (data?.logs || []));
    } catch (error) {
      console.error('TCS logs fetch failed:', error);
      setTcsLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchAnalytics();
    fetchTcsLogs();
  }, [fetchAnalytics, fetchTcsLogs]);

  const monthlyRows = useMemo(
    () =>
      Object.entries(monthly)
        .map(([month, v]) => ({ month, ...v }))
        .sort((a, b) => a.month.localeCompare(b.month)),
    [monthly],
  );

  return (
    <div className="space-y-6">
      {/* Empty-ledger banner — P1 #4 from FINANCE_GST_FORENSIC_AUDIT.md.
          Triggers when KPI summary is zero AND we suspect orders
          exist (we use the GST Filing Preview lane for the operational
          truth). */}
      {!loadingAnalytics && summary && (
        Number(summary.total_commission || 0) === 0 &&
        Number(summary.total_commission_gst || 0) === 0 &&
        Number(summary.total_tds_collected || 0) === 0 &&
        Number(summary.total_tcs_collected || 0) === 0
      ) && (
        <Card
          className="border-amber-300 bg-amber-50"
          data-testid="tds-tcs-empty-ledger-banner"
        >
          <CardContent className="p-4 flex items-start gap-3">
            <Info className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 text-sm">
              <div className="font-medium text-amber-900">
                Tax ledgers appear to be empty for this date range
              </div>
              <div className="text-xs text-gray-700 mt-1">
                If GST Filing Preview shows data while this tab shows ₹0, the
                forward-projection writer chain hasn&apos;t run on those orders.
                Open <span className="font-mono text-[11px]">Admin → Maintenance</span>{' '}
                and run the <span className="font-mono text-[11px]">finance-reconciliation-matrix</span>{' '}
                template to identify candidate orders, then{' '}
                <span className="font-mono text-[11px]">finance-backfill</span> with the
                opt-in list. See{' '}
                <span className="font-mono text-[11px]">FINANCE_GST_FORENSIC_AUDIT.md</span>.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-emerald-200 bg-emerald-50" data-testid="tds-tcs-banner">
        <CardContent className="p-4 flex items-start gap-3">
          <Landmark className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-medium">Marketplace Tax Liability Summary (SSOT)</div>
            <div className="text-xs text-gray-600 mt-1">
              Sources: <code>marketplace_revenue</code>, <code>tax_ledger</code>,{' '}
              <code>government_collections</code>. Date range applies to{' '}
              <code>recognised_at</code> / <code>created_at</code>.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 4 KPI tiles + 2 payout split tiles */}
      {loadingAnalytics ? (
        <div className="flex justify-center py-12" data-testid="tds-tcs-loading">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiTile
              label="Marketplace Commission Earned"
              value={formatINR(summary?.total_commission)}
              hint="marketplace_revenue (commission − reversal, netted)"
              icon={Banknote}
              accent="border-l-blue-500"
              testid="kpi-commission-earned"
            />
            <KpiTile
              label="GST Collected on Commission"
              value={formatINR(summary?.total_commission_gst)}
              hint="tax_ledger (commission_gst − reversal, netted)"
              icon={Percent}
              accent="border-l-purple-500"
              testid="kpi-commission-gst"
            />
            <KpiTile
              label="TDS Deducted (194-O)"
              value={formatINR(summary?.total_tds_collected)}
              hint="government_collections (TDS)"
              icon={Landmark}
              accent="border-l-amber-500"
              testid="kpi-tds-deducted"
            />
            <KpiTile
              label="TCS Collected (Sec 52)"
              value={formatINR(summary?.total_tcs_collected)}
              hint="government_collections (TCS)"
              icon={Landmark}
              accent="border-l-emerald-500"
              testid="kpi-tcs-collected"
            />
          </div>
          {/* Payout split — computed vs settled */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="border-l-4 border-l-amber-400" data-testid="kpi-payout-calculated">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">Payout Calculated (Computed)</div>
                    <div className="text-2xl font-bold mt-1 text-amber-700" data-testid="kpi-payout-calculated-value">
                      {formatINR(summary?.payout_calculated)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">vendor_payouts (status=computed) — calculated but NOT yet transferred</div>
                  </div>
                  <Banknote className="h-5 w-5 text-amber-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-600" data-testid="kpi-payout-released">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">Payout Released (Settled)</div>
                    <div className="text-2xl font-bold mt-1 text-green-700" data-testid="kpi-payout-released-value">
                      {formatINR(summary?.payout_released)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">vendor_payouts (status=settled) — actually transferred to vendor</div>
                  </div>
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Monthly breakdown */}
      {!loadingAnalytics && monthlyRows.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Monthly Breakdown</CardTitle>
            <CardDescription>
              Gross sales, TDS & TCS grouped by YYYY-MM
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tds-tcs-monthly-table">
                <thead className="border-b">
                  <tr className="text-left text-gray-500">
                    <th className="px-4 py-3">Month</th>
                    <th className="px-4 py-3">Gross Payout</th>
                    <th className="px-4 py-3">Transactions</th>
                    <th className="px-4 py-3">TDS</th>
                    <th className="px-4 py-3">TCS</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyRows.map((m) => (
                    <tr
                      key={m.month}
                      className="border-b hover:bg-gray-50"
                      data-testid={`tds-tcs-monthly-row-${m.month}`}
                    >
                      <td className="px-4 py-3 font-medium">{m.month}</td>
                      <td className="px-4 py-3">{formatINR(m.gross)}</td>
                      <td className="px-4 py-3 text-gray-600">{m.transactions}</td>
                      <td className="px-4 py-3 text-amber-700">{formatINR(m.tds)}</td>
                      <td className="px-4 py-3 text-emerald-700">{formatINR(m.tcs)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* TCS log detail */}
      <Card>
        <CardHeader>
          <CardTitle>TCS Log (most recent 200)</CardTitle>
          <CardDescription>
            Per-transaction TCS records from <code>db.tcs_logs</code>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingLogs ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : tcsLogs.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm" data-testid="tcs-logs-empty">
              No TCS log entries yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tcs-logs-table">
                <thead className="border-b">
                  <tr className="text-left text-gray-500">
                    <th className="px-4 py-3">Vendor</th>
                    <th className="px-4 py-3">Order</th>
                    <th className="px-4 py-3">Txn Amount</th>
                    <th className="px-4 py-3">TCS Rate</th>
                    <th className="px-4 py-3">TCS Amount</th>
                    <th className="px-4 py-3">GST?</th>
                    <th className="px-4 py-3">PAN?</th>
                    <th className="px-4 py-3">FY · Q</th>
                    <th className="px-4 py-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {tcsLogs.map((log) => (
                    <tr
                      key={log.id}
                      className="border-b hover:bg-gray-50"
                      data-testid={`tcs-log-row-${log.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium">{log.vendor_name || '—'}</div>
                        <div className="text-[10px] text-gray-500 font-mono">
                          {(log.vendor_id || '').slice(0, 8)}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {(log.order_id || '').slice(0, 8)}
                      </td>
                      <td className="px-4 py-3">{formatINR(log.transaction_amount)}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {log.tcs_rate ? `${log.tcs_rate}%` : '—'}
                      </td>
                      <td className="px-4 py-3 font-medium text-emerald-700">
                        {formatINR(log.tcs_amount)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={log.has_gst ? 'default' : 'outline'} className="text-[10px]">
                          {log.has_gst ? 'Yes' : 'No'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={log.has_pan ? 'default' : 'outline'} className="text-[10px]">
                          {log.has_pan ? 'Yes' : 'No'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">
                        {log.financial_year || '—'} · {log.quarter || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {formatDate(log.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ── Finance Ledger Tab ───────────────────────────────────────────────────
const DELIVERY_COLOR = {
  delivered:   'bg-emerald-100 text-emerald-800',
  fulfilled:   'bg-emerald-100 text-emerald-800',
  cancelled:   'bg-red-100 text-red-800',
  returned:    'bg-orange-100 text-orange-800',
  unknown:     'bg-gray-100 text-gray-600',
};
const PAYOUT_COLOR = {
  settled:   'bg-emerald-100 text-emerald-800',
  computed:  'bg-amber-100 text-amber-800',
  pending:   'bg-blue-100 text-blue-800',
  cancelled: 'bg-red-100 text-red-800',
};

const LedgerBadge = ({ value, colorMap, fallback = 'unknown' }) => {
  const key = (value || fallback).toLowerCase();
  const cls = colorMap[key] || colorMap[fallback] || 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-full ${cls}`}>
      {value || fallback}
    </span>
  );
};

const FinanceLedgerTab = ({ apiCall, toast, fromDate, toDate }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDelivery, setFilterDelivery] = useState('all');
  const [reportLoading, setReportLoading] = useState(false);

  const fetchLedger = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (fromDate) qs.append('start_date', fromDate);
      if (toDate) qs.append('end_date', toDate);
      const res = await apiCall(`/api/tax-management/admin/finance-ledger?${qs.toString()}`);
      setData(res);
    } catch (err) {
      console.error('Finance ledger fetch failed:', err);
      toast({ title: 'Error', description: 'Failed to load Finance Ledger', variant: 'destructive' });
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [apiCall, fromDate, toDate, toast]);

  useEffect(() => { fetchLedger(); }, [fetchLedger]);

  const rows = data?.rows || [];
  const summary = data?.summary || {};

  const filtered = useMemo(() => {
    let r = rows;
    if (filterDelivery !== 'all') {
      r = r.filter((row) => (row.delivery_status || '').toLowerCase() === filterDelivery);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      r = r.filter((row) =>
        (row.order_number || '').toLowerCase().includes(q) ||
        (row.vendor_name || '').toLowerCase().includes(q) ||
        (row.parent_order_number || '').toLowerCase().includes(q) ||
        (row.order_id || '').toLowerCase().includes(q)
      );
    }
    return r;
  }, [rows, searchQuery, filterDelivery]);

  const handleExportCSV = () => {
    if (!filtered.length) return;
    const cols = [
      'order_number','parent_order_number','vendor_name','order_date','order_status',
      'delivery_status','payout_status','gross','discount','net_sale','commission',
      'commission_gst','tds','tcs','pg_fee','pg_fee_gst','shipping_deduction',
      'net_payout_calculated','net_payout_released','escrow_status','escrow_release_at',
      'commission_invoice_ref','tax_invoice_ref','razorpay_transfer_id',
      'shipment_canonical','order_canonical_state','fulfillment_status',
    ];
    const header = cols.join(',');
    const csvRows = filtered.map((r) =>
      cols.map((c) => {
        const v = r[c];
        if (v === null || v === undefined) return '';
        const s = String(v);
        return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(',')
    );
    const blob = new Blob([header + '\n' + csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `finance-ledger-${fromDate || 'all'}-to-${toDate || 'today'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadReport = async () => {
    setReportLoading(true);
    try {
      const res = await apiCall('/api/tax-management/admin/finance-ledger-validation-report');
      const content = res?.report?.content || 'Report not available';
      const blob = new Blob([content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'FINANCE_LEDGER_VALIDATION_REPORT.md';
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Downloaded', description: 'FINANCE_LEDGER_VALIDATION_REPORT.md saved', variant: 'default' });
    } catch (err) {
      toast({ title: 'Error', description: 'Could not fetch validation report', variant: 'destructive' });
    } finally {
      setReportLoading(false);
    }
  };

  const deliveryCounts = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const k = (r.delivery_status || 'unknown').toLowerCase();
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [rows]);

  return (
    <div className="space-y-6" data-testid="finance-ledger-tab">
      {/* Authority banner */}
      <Card className="border-indigo-200 bg-indigo-50">
        <CardContent className="p-4 flex items-start gap-3">
          <Table2 className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 text-sm">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="font-medium text-indigo-900">Finance Ledger — Single-Row Authority View</div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-700 border border-red-200" data-testid="invoice-gate-badge">
                  <AlertTriangle className="h-3 w-3" /> INVOICE GEN: BLOCKED
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadReport}
                  disabled={reportLoading}
                  className="text-xs h-7"
                  data-testid="download-validation-report"
                >
                  {reportLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
                  Validation Report
                </Button>
              </div>
            </div>
            <div className="text-xs text-indigo-700 mt-1">
              Every column sourced per <code>FINANCE_LEDGER_AUTHORITY_MATRIX.md</code>.
              Delivery status uses <strong>Option-C compound gate</strong> (shipments fallback during D-074 migration).
              <br />
              <span className="text-amber-700 font-medium">
                Columns showing — mean the upstream data is not yet populated (see authority matrix for gaps).
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary KPIs */}
      {!loading && summary.total_gross !== undefined && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { label: 'Gross', value: formatINR(summary.total_gross), accent: 'border-l-blue-500', testid: 'ledger-kpi-gross' },
            { label: 'Commission', value: formatINR(summary.total_commission), accent: 'border-l-purple-500', testid: 'ledger-kpi-commission' },
            { label: 'Comm. GST', value: formatINR(summary.total_commission_gst), accent: 'border-l-violet-400', testid: 'ledger-kpi-commission-gst' },
            { label: 'TDS', value: formatINR(summary.total_tds), accent: 'border-l-amber-500', testid: 'ledger-kpi-tds' },
            { label: 'TCS', value: formatINR(summary.total_tcs), accent: 'border-l-orange-500', testid: 'ledger-kpi-tcs' },
            { label: 'Net Calc.', value: formatINR(summary.total_net_payout_calculated), accent: 'border-l-emerald-500', testid: 'ledger-kpi-net-calc' },
            { label: 'Net Released', value: formatINR(summary.total_net_payout_released), accent: 'border-l-green-700', testid: 'ledger-kpi-net-released' },
          ].map(({ label, value, accent, testid }) => (
            <Card key={label} className={`border-l-4 ${accent}`} data-testid={testid}>
              <CardContent className="p-3">
                <div className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</div>
                <div className="text-base font-bold mt-0.5" data-testid={`${testid}-value`}>{value}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search order #, vendor, parent order…"
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="ledger-search"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-gray-600">Delivery:</Label>
              {['all', 'delivered', 'unknown'].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilterDelivery(f)}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                    filterDelivery === f
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'border-gray-300 text-gray-600 hover:border-indigo-400'
                  }`}
                  data-testid={`ledger-filter-${f}`}
                >
                  {f === 'all' ? `All (${rows.length})` : `${f} (${deliveryCounts[f] || 0})`}
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              disabled={!filtered.length}
              data-testid="ledger-export-csv"
            >
              <Download className="h-4 w-4 mr-1" /> CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16" data-testid="finance-ledger-loading">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center" data-testid="finance-ledger-empty">
          <Table2 className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-700">No orders found</h3>
          <p className="text-gray-500 text-sm mt-1">
            {searchQuery || filterDelivery !== 'all' ? 'Try clearing filters' : 'No child orders in this date range'}
          </p>
        </Card>
      ) : (
        <Card data-testid="finance-ledger-table-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Table2 className="h-4 w-4 text-indigo-600" />
              Finance Ledger
            </CardTitle>
            <CardDescription data-testid="finance-ledger-count">
              {filtered.length} order{filtered.length !== 1 ? 's' : ''} · {fromDate || 'FY start'} → {toDate || 'today'}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="finance-ledger-table">
                <thead className="border-b bg-gray-50">
                  <tr className="text-left text-gray-500 uppercase tracking-wide text-[10px]">
                    {/* Identity */}
                    <th className="px-3 py-2 whitespace-nowrap">Order</th>
                    <th className="px-3 py-2 whitespace-nowrap">Parent</th>
                    <th className="px-3 py-2 whitespace-nowrap">Vendor</th>
                    <th className="px-3 py-2 whitespace-nowrap">Date</th>
                    {/* Operational */}
                    <th className="px-3 py-2 whitespace-nowrap bg-slate-50 border-l border-slate-200">Order St.</th>
                    <th className="px-3 py-2 whitespace-nowrap bg-slate-50">Delivery</th>
                    <th className="px-3 py-2 whitespace-nowrap bg-slate-50">Shipment CS</th>
                    <th className="px-3 py-2 whitespace-nowrap bg-slate-50 border-r border-slate-200">Order CS</th>
                    {/* Revenue */}
                    <th className="px-3 py-2 whitespace-nowrap text-right">Gross</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">Commission</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">Comm GST</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">TDS</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">TCS</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">PG Fee</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">Logistics</th>
                    {/* Settlement */}
                    <th className="px-3 py-2 whitespace-nowrap bg-emerald-50 border-l border-emerald-200 text-right">Net Calc.</th>
                    <th className="px-3 py-2 whitespace-nowrap bg-emerald-50 text-right">Net Released</th>
                    <th className="px-3 py-2 whitespace-nowrap bg-emerald-50">Payout St.</th>
                    <th className="px-3 py-2 whitespace-nowrap bg-emerald-50 border-r border-emerald-200">Escrow</th>
                    {/* Audit */}
                    <th className="px-3 py-2 whitespace-nowrap bg-violet-50 border-l border-violet-200">Comm Inv</th>
                    <th className="px-3 py-2 whitespace-nowrap bg-violet-50">Tax Inv</th>
                    <th className="px-3 py-2 whitespace-nowrap bg-violet-50">RZP Transfer</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <tr
                      key={row.order_id}
                      className="border-b hover:bg-gray-50/80 align-middle"
                      data-testid={`ledger-row-${row.order_number}`}
                    >
                      {/* Identity */}
                      <td className="px-3 py-2 font-medium whitespace-nowrap">{row.order_number || '—'}</td>
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{row.parent_order_number || '—'}</td>
                      <td className="px-3 py-2 whitespace-nowrap max-w-[120px] truncate" title={row.vendor_name}>
                        {row.vendor_name || '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{formatDate(row.order_date)}</td>
                      {/* Operational */}
                      <td className="px-3 py-2 bg-slate-50 border-l border-slate-200 whitespace-nowrap">
                        <LedgerBadge value={row.order_status} colorMap={{ cancelled: 'bg-red-100 text-red-800', fulfilled: 'bg-emerald-100 text-emerald-800', unfulfilled: 'bg-gray-100 text-gray-700' }} fallback="unknown" />
                      </td>
                      <td className="px-3 py-2 bg-slate-50 whitespace-nowrap">
                        <LedgerBadge value={row.delivery_status} colorMap={DELIVERY_COLOR} />
                      </td>
                      <td className="px-3 py-2 bg-slate-50 whitespace-nowrap">
                        {row.shipment_canonical
                          ? <span className="font-mono text-[10px] text-slate-600">{row.shipment_canonical}</span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2 bg-slate-50 border-r border-slate-200 whitespace-nowrap">
                        {row.order_canonical_state
                          ? <span className="font-mono text-[10px] text-slate-600">{row.order_canonical_state}</span>
                          : <span className="text-red-300 text-[10px]" title="D-074 Phase 2 gap — null until write-back runs">null</span>}
                      </td>
                      {/* Revenue */}
                      <td className="px-3 py-2 text-right font-medium whitespace-nowrap">{formatINR(row.gross)}</td>
                      <td className="px-3 py-2 text-right text-purple-700 whitespace-nowrap">{row.commission ? formatINR(row.commission) : <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 text-right text-purple-500 whitespace-nowrap">{row.commission_gst ? formatINR(row.commission_gst) : <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 text-right text-amber-700 whitespace-nowrap">{row.tds ? formatINR(row.tds) : <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 text-right text-orange-700 whitespace-nowrap">{row.tcs ? formatINR(row.tcs) : <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 text-right text-gray-600 whitespace-nowrap">{row.pg_fee ? formatINR(row.pg_fee) : <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 text-right text-gray-600 whitespace-nowrap">{row.shipping_deduction ? formatINR(row.shipping_deduction) : <span className="text-gray-300">—</span>}</td>
                      {/* Settlement */}
                      <td className="px-3 py-2 bg-emerald-50 border-l border-emerald-200 text-right font-medium whitespace-nowrap">
                        {row.net_payout_calculated ? formatINR(row.net_payout_calculated) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2 bg-emerald-50 text-right font-medium text-emerald-700 whitespace-nowrap">
                        {row.net_payout_released != null ? formatINR(row.net_payout_released) : <span className="text-gray-400 text-[10px]">not settled</span>}
                      </td>
                      <td className="px-3 py-2 bg-emerald-50 whitespace-nowrap">
                        <LedgerBadge value={row.payout_status} colorMap={PAYOUT_COLOR} fallback="none" />
                      </td>
                      <td className="px-3 py-2 bg-emerald-50 border-r border-emerald-200 whitespace-nowrap">
                        {row.escrow_status
                          ? <span className="font-mono text-[10px] text-emerald-700">{row.escrow_status}</span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      {/* Audit */}
                      <td className="px-3 py-2 bg-violet-50 border-l border-violet-200 whitespace-nowrap">
                        {row.commission_invoice_ref
                          ? <span className="font-mono text-[10px] text-violet-700">{row.commission_invoice_ref}</span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2 bg-violet-50 whitespace-nowrap">
                        {row.tax_invoice_ref
                          ? <span className="font-mono text-[10px] text-violet-700">{row.tax_invoice_ref}</span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2 bg-violet-50 whitespace-nowrap">
                        {row.razorpay_transfer_id
                          ? <span className="font-mono text-[10px] text-violet-700">{row.razorpay_transfer_id}</span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {data?.authority_note && (
        <p className="text-xs text-gray-400 italic" data-testid="finance-ledger-note">{data.authority_note}</p>
      )}
    </div>
  );
};

// ── Main page ────────────────────────────────────────────────────────────
const AdminTaxInvoices = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();

  // Date range — default to current FY → today, applied across all tabs.
  const [fromDate, setFromDate] = useState(currentFYStartISO());
  const [toDate, setToDate] = useState(todayISO());
  const [appliedFrom, setAppliedFrom] = useState(currentFYStartISO());
  const [appliedTo, setAppliedTo] = useState(todayISO());
  const [activeTab, setActiveTab] = useState('tax-invoices');
  const [refreshKey, setRefreshKey] = useState(0);

  const applyDates = () => {
    setAppliedFrom(fromDate);
    setAppliedTo(toDate);
  };
  const resetDates = () => {
    const f = currentFYStartISO();
    const t = todayISO();
    setFromDate(f);
    setToDate(t);
    setAppliedFrom(f);
    setAppliedTo(t);
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="admin-tax-invoices-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="h-6 w-6" />
              Tax &amp; Commission Center
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              SSOT view of all tax invoices, commission invoices, and TDS/TCS ledgers
            </p>
          </div>
          <Button
            onClick={() => setRefreshKey((k) => k + 1)}
            variant="outline"
            size="sm"
            data-testid="admin-tax-invoices-refresh"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        <DateRangeFilter
          fromDate={fromDate}
          toDate={toDate}
          setFromDate={setFromDate}
          setToDate={setToDate}
          onApply={applyDates}
          onReset={resetDates}
        />

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList data-testid="admin-tax-tabs">
            <TabsTrigger value="tax-invoices" data-testid="tab-tax-invoices">
              Tax Invoices
            </TabsTrigger>
            <TabsTrigger value="commission-invoices" data-testid="tab-commission-invoices">
              Commission Invoices
            </TabsTrigger>
            <TabsTrigger value="tds-tcs" data-testid="tab-tds-tcs">
              TDS/TCS Ledgers
            </TabsTrigger>
            <TabsTrigger value="projected-gst" data-testid="tab-projected-gst">
              Projected GST
            </TabsTrigger>
            <TabsTrigger value="finance-ledger" data-testid="tab-finance-ledger">
              Finance Ledger
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tax-invoices" className="mt-6">
            <TaxInvoicesTab
              key={`tax-${appliedFrom}-${appliedTo}-${refreshKey}`}
              apiCall={apiCall}
              toast={toast}
              fromDate={appliedFrom}
              toDate={appliedTo}
            />
          </TabsContent>

          <TabsContent value="commission-invoices" className="mt-6">
            <CommissionInvoicesTab
              key={`comm-${refreshKey}`}
              apiCall={apiCall}
              toast={toast}
            />
          </TabsContent>

          <TabsContent value="tds-tcs" className="mt-6">
            <TdsTcsTab
              key={`tds-${appliedFrom}-${appliedTo}-${refreshKey}`}
              apiCall={apiCall}
              toast={toast}
              fromDate={appliedFrom}
              toDate={appliedTo}
            />
          </TabsContent>

          <TabsContent value="projected-gst" className="mt-6">
            <ProjectedGstTab
              key={`pgst-${appliedFrom}-${appliedTo}-${refreshKey}`}
              apiCall={apiCall}
              toast={toast}
              fromDate={appliedFrom}
              toDate={appliedTo}
            />
          </TabsContent>

          <TabsContent value="finance-ledger" className="mt-6">
            <FinanceLedgerTab
              key={`ledger-${appliedFrom}-${appliedTo}-${refreshKey}`}
              apiCall={apiCall}
              toast={toast}
              fromDate={appliedFrom}
              toDate={appliedTo}
            />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AdminTaxInvoices;
